/**
 * Find in Store Action
 * Triggered by: arc:action:findInStore
 */

import shopifyHelper from "../../js/shopify";
import { hasher } from "../../js/alpine/stores/hasher";
import GET_INVENTORY_ITEMS from '../../js/graphql/2024_01/inventory/get.graphql';

// Global state for inventory and delivery information
let inventoryLevels = null;
let deliveryMethods = null;
const useAuthenticated = true;

export const runFindInStore = async (data = {}) => {
    try {
        const { 
            action = 'getStock', // 'getStock', 'getDeliveryMethods', 'both'
            sku,
            variantId,
            useAuth = useAuthenticated,
            includeDeliveryMethods = false 
        } = data;

        let results = {};

        switch (action) {
            case 'getStock':
                if (!sku && !variantId) {
                    throw new Error('Either SKU or variantId is required for stock lookup');
                }
                results.stock = await getStockForProduct(sku || variantId, useAuth);
                break;
                
            case 'getDeliveryMethods':
                results.delivery = await getDeliveryMethodsShopify();
                break;
                
            case 'both':
                if (!sku && !variantId) {
                    throw new Error('Either SKU or variantId is required for stock lookup');
                }
                results.stock = await getStockForProduct(sku || variantId, useAuth);
                results.delivery = await getDeliveryMethodsShopify();
                break;
                
            default:
                throw new Error(`Unknown action: ${action}`);
        }

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('store:found', {
            detail: {
                action,
                sku: sku || variantId,
                inventoryLevels,
                deliveryMethods,
                results
            }
        }));

        return {
            success: true,
            action,
            sku: sku || variantId,
            inventoryLevels,
            deliveryMethods,
            results,
            message: 'Store information retrieved successfully'
        };

    } catch (error) {
        console.error('Find in Store error:', error);
        
        window.dispatchEvent(new CustomEvent('store:error', {
            detail: {
                error: error.message,
                action: 'findInStore'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Get stock information for a product
const getStockForProduct = async (sku, useAuth = true) => {
    try {
        console.log('Retrieving Product Stock for SKU:', sku);
        
        const body = JSON.stringify({
            query: GET_INVENTORY_ITEMS.loc.source.body,
            variables: {
                query: `sku:'${sku}'`
            }
        });

        let data = {
            options: {
                method: 'POST',
                headers: {
                    'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
                    'Content-Type': 'application/json',
                    'X-MindArc-Hash': hasher.store().getHash()
                },
                body: body,
                request_body: GET_INVENTORY_ITEMS.loc.source.body,
            }
        };

        let jsonResponse = null;
        if (useAuth) {
            jsonResponse = await shopifyHelper.authenticatedFetch(data);
        } else {
            jsonResponse = await shopifyHelper.unauthenticatedFetch(body);
        }

        const inventoryEdges = jsonResponse.data?.inventoryItems?.edges[0]?.node?.inventoryLevels?.edges;

        if (inventoryEdges) {
            let levels = [];
            
            inventoryEdges.forEach((inventoryLevel) => {
                const node = inventoryLevel.node;
                const location_name = node.location.name;
                let qty = 0;
                let stock_type = 'available';

                node.quantities.forEach((quantity) => {
                    qty = quantity.quantity;
                    stock_type = quantity.name;
                });

                levels.push({
                    name: location_name,
                    qty: qty,
                    stock_type: stock_type,
                    stock_status: qty > 0,
                    location: node.location
                });
            });

            inventoryLevels = levels;

            // Dispatch legacy event for backward compatibility
            window.dispatchEvent(new Event('findinstore-search', { bubbles: true }));

            return jsonResponse;
        } else {
            inventoryLevels = [];
            return null;
        }

    } catch (error) {
        console.error('Error fetching stock information:', error);
        inventoryLevels = [];
        throw error;
    }
};

// Get delivery methods from Shopify
const getDeliveryMethodsShopify = async () => {
    try {
        console.log('Retrieving Delivery Methods');
        
        const deliveryMethodQuery = `query {
          deliveryProfiles (first: 2) {
            edges {
              node {
                name
                profileLocationGroups {
                  locationGroupZones(first: 2) {
                    edges {
                      node {
                        methodDefinitions(first: 4) {
                          edges {
                            node {
                                name
                                description
                                rateProvider {
                                ... on DeliveryRateDefinition {
                                  id
                                  price {
                                    amount
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }`;

        const jsonResponse = await shopifyHelper.unauthenticatedFetch(deliveryMethodQuery);
        
        const methodDefinitions = jsonResponse.data?.deliveryProfiles?.edges[0]?.node?.profileLocationGroups[0]?.locationGroupZones?.edges[0]?.node?.methodDefinitions?.edges;

        if (methodDefinitions) {
            let methods = [];

            methodDefinitions.forEach((method) => {
                const node = method.node;
                methods.push({
                    name: node.name,
                    price: node.rateProvider?.price?.amount || 0,
                    description: node.description
                });
            });

            deliveryMethods = methods;

            // Dispatch legacy event for backward compatibility
            window.dispatchEvent(new Event('findinstore-search', { bubbles: true }));

            return jsonResponse;
        } else {
            deliveryMethods = [];
            return null;
        }

    } catch (error) {
        console.error('Error fetching delivery methods:', error);
        deliveryMethods = [];
        throw error;
    }
};

// Get current inventory levels
export const getInventoryLevels = () => {
    return inventoryLevels;
};

// Get current delivery methods
export const getDeliveryMethods = () => {
    return deliveryMethods;
};

// Clear current data
export const clearStoreData = () => {
    inventoryLevels = null;
    deliveryMethods = null;
    
    window.dispatchEvent(new CustomEvent('store:cleared', {
        detail: {
            timestamp: Date.now()
        }
    }));
};

// Subscribe to store lookup events (backward compatibility)
export const subscribeToStoreLookup = (callback) => {
    window.addEventListener('findinstore-search', callback);
    return () => {
        window.removeEventListener('findinstore-search', callback);
    };
};

// Initialize find in store system
export const initializeFindInStore = (config = {}) => {
    const {
        autoSetupForms = true,
        stockFormSelector = '[data-stock-lookup-form]',
        skuInputSelector = '[data-sku-input]',
        submitSelector = '[data-stock-submit]'
    } = config;

    if (autoSetupForms) {
        setupStockLookupForms(stockFormSelector, skuInputSelector, submitSelector);
    }

    // Set up global access (backward compatibility)
    window.findinstore = {
        getInventoryLevels,
        getDeliveryMethods,
        subscribe: subscribeToStoreLookup
    };

    // Dispatch initialization event
    window.dispatchEvent(new CustomEvent('findInStore:initialized', {
        detail: {
            config,
            timestamp: Date.now()
        }
    }));
};

// Setup stock lookup form event listeners
const setupStockLookupForms = (formSelector, inputSelector, submitSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        const input = form.querySelector(inputSelector);
        const submitBtn = form.querySelector(submitSelector);

        if (input && submitBtn) {
            // Remove existing listeners
            submitBtn.removeEventListener('click', handleStockLookupSubmit);
            form.removeEventListener('submit', handleStockLookupSubmit);

            // Add new listeners
            submitBtn.addEventListener('click', handleStockLookupSubmit);
            form.addEventListener('submit', handleStockLookupSubmit);
        }
    });
};

// Handle stock lookup form submissions
const handleStockLookupSubmit = (event) => {
    event.preventDefault();
    
    const form = event.target.closest('[data-stock-lookup-form]');
    const input = form?.querySelector('[data-sku-input]');
    const skuValue = input?.value?.trim();

    if (skuValue) {
        window.dispatchEvent(new CustomEvent('arc:action:findInStore', {
            detail: {
                action: 'getStock',
                sku: skuValue
            }
        }));
    }
};

// Helper function to get stock for current variant
export const getCurrentVariantStock = () => {
    const currentVariant = window.currentVariant;
    if (currentVariant) {
        const variantElement = document.querySelector(`input[data-variant-id="${currentVariant}"]`);
        const sku = variantElement?.getAttribute("data-variant-sku");
        
        if (sku) {
            window.dispatchEvent(new CustomEvent('arc:action:findInStore', {
                detail: {
                    action: 'getStock',
                    sku: sku
                }
            }));
        }
    }
};

export default runFindInStore;
